import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';
import { DialogService, DynamicDialogRef } from '../../../core/services/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { filter } from 'rxjs/operators';
import {
  DEFAULT_EMAIL_TABS,
  DEFAULT_MENUS,
  DEFAULT_SETTINGS_TABS,
  DEFAULT_TICKETS_TABS,
  FooterMenu,
  getBrokerDetailsTabs,
  getClientDetailsTabs,
  getDashboardTabs,
  getDeclarationCreateTabs,
  getDeclarationsDetailsTabs,
  getDeclarationsEditTabs,
  getEmcsCreateTabs,
  getEmcsDetailsTabs,
  getEmcsTabs,
  getExportTabs,
  getImportTabs,
  getRtoShipmentDetailsTabs,
  getRtoTabs,
  getShipmentDetailsTabs,
  getTransitArrivalCreateTabs,
  getTransitArrivalDetailsTabs,
  getTransitArrivalEditTabs,
  getTransitDepartureCreateTabs,
  getTransitDepartureDetailsTabs,
  NavigationConfigDeps,
  TabItem,
} from '../../../configs/navigation.config';
import { AlertService } from '../../../core/services/alert/alert.service';
import { CacheService } from '../../../core/services/cache/cache.service';
import { DashboardService } from '../../../core/services/dashboard/dashboard.service';
import { DataService } from '../../../core/services/data/data.service';
import { DeclarationService } from '../../../core/services/declarations/declaration.service';
import { LayoutService } from '../../../core/services/layout/layout.service';
import { resolveOperationalLandingPath } from '../../../core/guards/operational-landing.utils';
import { RoleService } from '../../../core/services/role/role.service';
import { RtoAccessService } from '../../../core/services/rto/rto-access.service';
import { ShipmentService } from '../../../core/services/shipment/shipment.service';
import { UpdatesComponent } from '../../../pages/updates/updates-component';
import { AccessibleClickDirective } from '../../directives/accessible-click.directive';
import { DeclarationProcedureType, UserType } from '../../interfaces';
import { TranslatePipe } from '../../pipes/translate/translate.pipe';
import { UserGuidanceService } from '../../services/analytics/user-guidance.service';
import { QuickViewService } from '../../services/quick-view/quick-view.service';
import { IcChevronDownComponent } from '../Icons/ic-chevron-down/ic-chevron-down';
import { IcEmcsComponent } from '../Icons/ic-emcs/ic-emcs';
import { IcExportComponent } from '../Icons/ic-export/ic-export';
import { IcGlobeComponent } from '../Icons/ic-globe/ic-globe';
import { IcGraphComponent } from '../Icons/ic-graph/ic-graph.component';
import { IcHomeComponent } from '../Icons/ic-home';
import { IcImportComponent } from '../Icons/ic-import/ic-import';
import { IcParcelBoxComponent } from '../Icons/ic-parcelbox/ic-parcelbox';
import { IcPlaneComponent } from '../Icons/ic-plane/ic-plane';
import { IcTasksComponent } from '../Icons/ic-tasks/ic-tasks.component';
import { IcTransitComponent } from '../Icons/ic-transit/ic-transit';
import { IcWarehouseComponent } from '../Icons/ic-warehouse/ic-warehouse';
import { IcCommodityClassifierComponent } from '../Icons/ic-commodity-classifier/ic-commodity-classifier';
import { IcDeclarationTemplate } from '../Icons/settings-icon/ic-declaration-template/ic-declaration-template';
import { FooterTabComponent } from './footer-tab/footer-tab.component';
/**
 * FooterComponent component.
 * Provides UI presentation and event handling for footer component.
 */
@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [
    AccessibleClickDirective,
    CommonModule,
    RouterModule,
    IcHomeComponent,
    IcPlaneComponent,
    IcGlobeComponent,
    IcParcelBoxComponent,
    IcDeclarationTemplate,
    IcTransitComponent,
    IcWarehouseComponent,
    IcEmcsComponent,
    IcTasksComponent,
    FooterTabComponent,
    MatTooltipModule,
    TranslatePipe,
    IcChevronDownComponent,
    IcImportComponent,
    IcExportComponent,
    IcGraphComponent,
    IcWarehouseComponent,
    IcEmcsComponent,
    IcCommodityClassifierComponent,
  ],
  templateUrl: './footer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: block;
        height: 0;
        padding: 0;
        margin: 0;
      }
    `,
  ],
})
export class FooterComponent implements OnInit {
  /** Property active menu. */
  activeMenu = 'import';
  /** Property show tabs. */
  showTabs = signal<boolean>(false);
  /** Property is details page. */
  isDetailsPage = signal<boolean>(false);
  /** Property current tabs. */
  currentTabs = signal<TabItem[]>([]);
  /** Property is tab method. */
  private isTabMethod = signal<boolean>(false);
  /** Property drawer visible. */
  drawerVisible = false;
  /** Property drawer type. */
  drawerType: string | null = null;
  /** Injected service for guidance service. */
  guidanceService = inject(UserGuidanceService);
  /** Injected service for quick view service. */
  quickViewService = inject(QuickViewService);
  /** Injected service for dialog service. */
  private dialogService = inject(DialogService);
  /** Injected service for data service. */
  private dataService = inject(DataService);
  /** Injected service for layout service. */
  public layoutService = inject(LayoutService);
  /** Injected service for shipment service. */
  private shipmentService = inject(ShipmentService);
  /** Injected service for declaration service. */
  private declarationService = inject(DeclarationService);
  /** Injected service for cache service. */
  private cacheService = inject(CacheService);
  /** Injected service for role service. */
  private roleService = inject(RoleService);
  /** Injected service for rto access service. */
  private rtoAccessService = inject(RtoAccessService);
  /** Injected service for dashboard service. */
  private dashboardService = inject(DashboardService);
  /** Injected service for alert service. */
  private alertService = inject(AlertService);
  /** Property dialog ref. */
  private dialogRef: DynamicDialogRef | undefined | null;

  /** Property default dashboard. */
  defaultDashboard = computed(() => this.dashboardService.defaultDashboard());

  constructor(private router: Router, private route: ActivatedRoute) {
    // Re-initialize tabs if procedureType, declarationEditMode, or layout changes
    effect(() => {
      this.dataService.procedureType();
      this.dataService.declarationEditMode();
      this.dataService.shipmentCounts();
      this.dataService.errorCounts();
      this.dataService.currentShipment();
      this.dataService.listBreadcrumbContext();
      this.dataService.transitStatusCode();
      this.layoutService.activeLayout();
      this.initializeFromRoute(this.router.url);
    });
  }

  /**
   * Handles is h7 logic.
   */
  isH7() {
    const procType = this.dataService.procedureType();
    return procType === 'H7' || procType === DeclarationProcedureType.DECO;
  }

  /** Property is shipment locked. */
  isShipmentLocked = computed(() => {
    const s = this.dataService.currentShipment();
    return s?.isManuallyOverridden === true;
  });

  /**
   * Lifecycle hook that runs on component initialization.
   */
  ngOnInit() {
    this.filterMenus();
    this.fetchErrorCounts();

    // Initialize based on current route
    this.initializeFromRoute(this.router.url);

    // Listen to route changes
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe((event: NavigationEnd) => {
        this.initializeFromRoute(event.urlAfterRedirects);
        // Check for tabMethod query parameter
        this.checkTabMethod();
      });

    // Initial check for tabMethod
    this.checkTabMethod();
    this.refreshLastViewed();
  }

  /**
   * Handles filter menus logic.
   */
  private filterMenus() {
    const user = this.cacheService.currentUser();
    if (!user) return;

    // Filter top-level menus by permission
    let filteredMenus = DEFAULT_MENUS.filter((menu) => {
      if (!menu.permission) return true;
      return this.roleService.isPermitted(menu.permission);
    });

    // Deep clone the settings menu tabs if present, and filter them
    filteredMenus = filteredMenus.map((menu) => {
      if (menu.id === 'settings' && menu.tabs) {
        let settingsTabs = [...menu.tabs];
        // Client users never see Brokers; Clients stays when client.read is granted.
        if (user.userType === UserType.client) {
          settingsTabs = settingsTabs.filter((t) => t.label !== 'brokers');
        }
        // Broker and client logins do not manage roles.
        if (
          user.userType === UserType.broker ||
          user.userType === UserType.client
        ) {
          settingsTabs = settingsTabs.filter((t) => t.label !== 'roles');
        }
        // Also apply role permissions
        settingsTabs = settingsTabs.filter((tab) => {
          if (!tab.permission) return true;
          return this.roleService.isPermitted(tab.permission);
        });
        return { ...menu, tabs: settingsTabs };
      }
      return menu;
    });

    this.menus = filteredMenus;

    // Refresh current tabs if we are already on the settings menu
    if (
      this.activeMenu === 'settings' &&
      !this.router.url.includes('/details/')
    ) {
      const settingsMenu = this.menus.find((m) => m.id === 'settings');
      if (settingsMenu && settingsMenu.tabs) {
        this.currentTabs.set(settingsMenu.tabs);
      }
    }
  }

  /**
   * Handles refresh last viewed logic.
   */
  private refreshLastViewed() {
    // Current route history is now managed by the WidgetService and WidgetMenu
  }

  /**
   * Handles fetch error counts logic.
   */
  private fetchErrorCounts() {
    const alertFilter = JSON.stringify({ alertCount: { gt: 0 } });

    // Sync shipment error counts using alert-priority endpoint with same filter as list
    this.shipmentService
      .getShipmentsSortedByAlerts({
        itemsPerPage: 1,
        page: 1,
        specificNumericFields: alertFilter,
      })
      .subscribe((res) => {
        if (res.success && res.data) {
          const shipmentCount =
            (res.data as any).itemCount ?? (res.data as any).itemsCount ?? 0;
          this.dataService.errorCounts.update((c) => ({
            ...c,
            shipments: shipmentCount,
          }));
        }
      });

    // Sync declaration error counts using alert-priority endpoint with same filter as list
    this.declarationService
      .getDeclarationsSortedByAlerts({
        itemsPerPage: 1,
        page: 1,
        specificNumericFields: alertFilter,
      })
      .subscribe((res) => {
        if (res.success && res.data) {
          const declarationCount =
            (res.data as any).itemCount ?? (res.data as any).itemsCount ?? 0;
          this.dataService.errorCounts.update((c) => ({
            ...c,
            declarations: declarationCount,
          }));
        }
      });
  }

  /**
   * Handles check tab method logic.
   */
  private checkTabMethod() {
    // Get query params from current URL
    const urlTree = this.router.parseUrl(this.router.url);
    const tabMethodParam = urlTree.queryParams['tabMethod'];
    this.isTabMethod.set(tabMethodParam === 'true');
  }

  /**
   * Extract a clean path from any URL string, stripping query params and fragments.
   */
  private getCleanPath(url: string): string {
    return url.split('?')[0].split('#')[0];
  }

  /**
   * Handles initialize from route logic.
   * @param url - The url
   */
  private initializeFromRoute(url: string) {
    // Always work with clean path (no query params)
    let cleanUrl = this.getCleanPath(url);
    if (cleanUrl === '/shipments/import') {
      cleanUrl = '/import/shipments';
    } else if (cleanUrl === '/shipments/export') {
      cleanUrl = '/export/shipments';
    } else if (cleanUrl === '/declarations/import') {
      cleanUrl = '/import/declarations';
    } else if (cleanUrl === '/declarations/export') {
      cleanUrl = '/export/declarations';
    }

    let matchedMenu: FooterMenu | undefined;
    let matchedTab: TabItem | undefined;

    this.isDetailsPage.set(
      cleanUrl.includes('details') || cleanUrl.includes('edit')
    );

    const isEditPageWithoutTabs =
      cleanUrl.includes('/settings/manifest-templates/edit') ||
      cleanUrl.includes('/settings/manifest-templates/new') ||
      cleanUrl.includes('/settings/download-templates/edit') ||
      cleanUrl.includes('/settings/download-templates/new') ||
      cleanUrl.includes('/settings/roles/edit') ||
      cleanUrl.includes('/settings/roles/new') ||
      cleanUrl.includes('/settings/users/edit') ||
      cleanUrl.includes('/settings/users/new') ||
      cleanUrl.includes('/settings/brokers/edit') ||
      cleanUrl.includes('/settings/brokers/new') ||
      cleanUrl.includes('/settings/clients/edit') ||
      cleanUrl.includes('/settings/clients/new') ||
      cleanUrl.includes('/settings/parties/edit') ||
      cleanUrl.includes('/settings/parties/new') ||
      cleanUrl.includes('/settings/parties/details') ||
      cleanUrl.includes('/settings/declaration-templates/details') ||
      cleanUrl.includes('/settings/declaration-templates/edit') ||
      cleanUrl.includes('/settings/declaration-templates/new') ||
      cleanUrl.includes('/settings/declaration-templates/transit/edit') ||
      cleanUrl.includes('/settings/declaration-templates/transit/new');

    const isTransitCreatePage =
      cleanUrl.includes('/transit-declarations/transit-arrival/create') ||
      cleanUrl.includes('/transit-declarations/transit-departure/create');

    this.layoutService.isFooterTabsDisabled.set(
      isEditPageWithoutTabs ||
        isTransitCreatePage ||
        this.layoutService.navigationMode() === 'side-menu'
    );

    // Template create/edit is a sequential details page — never show footer tabs.
    if (
      cleanUrl.includes('/settings/declaration-templates/edit') ||
      cleanUrl.includes('/settings/declaration-templates/new') ||
      cleanUrl.includes('/settings/declaration-templates/transit/edit') ||
      cleanUrl.includes('/settings/declaration-templates/transit/new') ||
      cleanUrl.includes('/settings/download-templates/edit') ||
      cleanUrl.includes('/settings/download-templates/new')
    ) {
      this.showTabs.set(false);
      this.currentTabs.set([]);
      this.activeTab.set('');
      this.activeMenu = 'settings';
      this.dataService.setActiveModule('settings');
      return;
    }

    // 1. First try to match tabs from all menus (most specific match first)
    const candidates: { menu: FooterMenu; tab: TabItem }[] = [];
    for (const menu of this.menus) {
      if (menu.tabs) {
        menu.tabs
          .filter((t) => t.route && cleanUrl.includes(t.route))
          .forEach((t) => candidates.push({ menu, tab: t }));
      }
    }

    // Add detail tabs which aren't in the main menus array search scope
    const addCandidates = (tabs: TabItem[], menuId: string) => {
      const menu = this.menus.find((m) => m.id === menuId);
      if (menu) {
        tabs
          .filter((t) => {
            if (!t.route) return false;
            const matchesRoute = cleanUrl.includes(t.route);
            // Verify this menu actually belongs to this URL path
            const isMenuMatch =
              cleanUrl.includes(`/${menuId}/`) ||
              (menuId === 'transit' &&
                cleanUrl.includes('/transit-declarations/')) ||
              (menuId === 'settings' && cleanUrl.includes('/settings/'));

            return matchesRoute && isMenuMatch;
          })
          .forEach((t) => candidates.push({ menu, tab: t }));
      }
    };

    addCandidates(this.getShipmentDetailsTabs(), 'import');
    addCandidates(this.getShipmentDetailsTabs(), 'export');
    addCandidates(this.getDeclarationsDetailsTabs(), 'import');
    addCandidates(this.getDeclarationsDetailsTabs(), 'export');
    addCandidates(this.getDeclarationsEditTabs(), 'import');
    addCandidates(this.getDeclarationsEditTabs(), 'export');
    addCandidates(this.getTransitTabs(), 'transit');
    addCandidates(this.getBrokerDetailsTabs(), 'settings');
    addCandidates(this.getClientDetailsTabs(), 'settings');

    // Create/edit/details transit pages use their own tab sets. Exclude them from
    // list-tab candidate matching so `/transit-departure/create` does not match
    // the list route `/transit-departure` via `includes`.
    const isTransitFormPage =
      cleanUrl.includes('/transit-declarations/transit-arrival/create') ||
      cleanUrl.includes('/transit-declarations/transit-departure/create') ||
      cleanUrl.includes('/transit-declarations/transit-arrival/edit/') ||
      cleanUrl.includes('/transit-declarations/transit-departure/edit/') ||
      cleanUrl.includes('/transit-declarations/transit-arrival/details/') ||
      cleanUrl.includes('/transit-declarations/transit-departure/details/');

    // Same for EMCS create/edit/details pages, which use their own tab sets.
    const isEmcsFormPage =
      cleanUrl.includes('/emcs/create') ||
      cleanUrl.includes('/emcs/edit/') ||
      cleanUrl.includes('/emcs/details/');

    const isRtoDetailsPage = cleanUrl.includes('/rto/shipments/details/');

    if (
      candidates.length > 0 &&
      !isTransitFormPage &&
      !isEmcsFormPage &&
      !isRtoDetailsPage
    ) {
      // Sort by route length descending to get the most specific match first
      candidates.sort(
        (a, b) => (b.tab.route?.length || 0) - (a.tab.route?.length || 0)
      );
      matchedMenu = candidates[0].menu;
      matchedTab = candidates[0].tab;
    }

    // 2. Fallback to matching menu route if no tab matched
    if (!matchedMenu) {
      const sortedMenus = [...this.menus].sort(
        (a, b) => b.route.length - a.route.length
      );
      const activeMod = this.dataService.activeModule();
      let resolvedMod = activeMod;

      const isTransitUrl =
        cleanUrl === '/transit-declarations' ||
        cleanUrl.startsWith('/transit-declarations/');
      const isEmcsUrl = cleanUrl.startsWith('/emcs');
      const isDetailUrl =
        !isTransitUrl &&
        !isEmcsUrl &&
        !cleanUrl.startsWith('/settings') &&
        (cleanUrl.includes('details') || cleanUrl.includes('edit'));

      if (isDetailUrl) {
        const shipment = this.dataService.currentShipment();
        if (shipment?.shipmentType) {
          resolvedMod = shipment.shipmentType.toLowerCase();
        } else {
          const ctx = this.dataService.listBreadcrumbContext();
          if (ctx?.route?.includes('/export/')) {
            resolvedMod = 'export';
          } else if (ctx?.route?.includes('/import/')) {
            resolvedMod = 'import';
          } else {
            resolvedMod = 'import';
          }
        }
      }

      const isShipmentOrDeclarationUrl =
        cleanUrl.startsWith('/shipments') ||
        cleanUrl.startsWith('/declarations');

      if (isShipmentOrDeclarationUrl) {
        const ctx = this.dataService.listBreadcrumbContext();
        if (
          ctx?.route?.includes('/export/') ||
          ctx?.itemName?.includes('export')
        ) {
          resolvedMod = 'export';
        } else if (
          ctx?.route?.includes('/import/') ||
          ctx?.itemName?.includes('import')
        ) {
          resolvedMod = 'import';
        } else if (resolvedMod !== 'export') {
          resolvedMod = 'import';
        }
      }

      if (
        cleanUrl.startsWith('/notifications') ||
        cleanUrl.startsWith('/alerts')
      ) {
        matchedMenu = undefined;
      } else if (cleanUrl.startsWith('/dashboard')) {
        matchedMenu = this.menus.find((m) => m.id === 'dashboard');
      } else if (cleanUrl.startsWith('/rto')) {
        matchedMenu = this.menus.find((m) => m.id === 'rto');
      } else if (cleanUrl.startsWith('/commodity-classifier')) {
        matchedMenu = this.menus.find((m) => m.id === 'commodity-classifier');
      } else if (isTransitUrl) {
        matchedMenu = this.menus.find((m) => m.id === 'transit');
      } else if (isEmcsUrl) {
        matchedMenu = this.menus.find((m) => m.id === 'emcs');
      } else if (resolvedMod === 'import' || resolvedMod === 'export') {
        matchedMenu = this.menus.find((m) => m.id === resolvedMod);
      } else if (cleanUrl.includes('/import/')) {
        matchedMenu = this.menus.find((m) => m.id === 'import');
      } else if (cleanUrl.includes('/export/')) {
        matchedMenu = this.menus.find((m) => m.id === 'export');
      } else if (cleanUrl.startsWith('/tickets')) {
        matchedMenu = this.menus.find((m) => m.id === 'tasks');
      } else if (
        cleanUrl.startsWith('/settings') ||
        cleanUrl.startsWith('/email')
      ) {
        matchedMenu = this.menus.find((m) => m.id === 'settings');
      } else {
        matchedMenu = sortedMenus.find((m) => cleanUrl.includes(m.route));
      }
    }

    if (matchedMenu) {
      this.activeMenu = matchedMenu.id;

      // Sync activeModule signal for non-detail pages so breadcrumbs stay
      // accurate after browser back/forward and link-based navigation.
      // Detail pages are excluded to preserve cross-module context
      // (e.g. Shipments › #S001 › #LRN when opening a linked declaration).
      if (!cleanUrl.includes('/details/')) {
        this.dataService.setActiveModule(matchedMenu.id);
      }

      // Update displayed tabs based on section
      let newTabs: TabItem[] = [];
      const baseUrl = this.getDetailsBaseUrl(cleanUrl);

      if (matchedMenu.id === 'import' || matchedMenu.id === 'export') {
        this.showTabs.set(true);
        if (cleanUrl.includes('/shipments/details')) {
          newTabs = this.getShipmentDetailsTabs();
        } else if (
          cleanUrl.includes('/declarations/details') &&
          this.dataService.declarationEditMode()
        ) {
          // In inline edit mode, tabs must have NO route so onTabChange always
          // dispatches the footer-tab-change event and returns immediately.
          newTabs = this.getDeclarationsEditTabs().map((t) => ({
            ...t,
            route: undefined,
          }));

          // Preserve active tab functional mapping if coming from details
          const currentPath = cleanUrl;
          let nextTabId = '1'; // Default: Import

          if (currentPath.includes('/info')) nextTabId = '1';
          else if (currentPath.includes('/import')) nextTabId = '1';
          else if (currentPath.includes('/financial')) nextTabId = '2';
          else if (currentPath.includes('/custom-details')) nextTabId = '3';
          else if (currentPath.includes('/delivery-items'))
            nextTabId = this.isH7() ? '3' : '4';

          this.currentTabs.set(newTabs);
          this.activeTab.set(nextTabId);
          return; // Exit early so fallback matching doesn't overwrite nextTabId
        } else if (cleanUrl.includes('/declarations/details/create')) {
          // Alt create is a single-page draft layout (no footer tab sections).
          this.showTabs.set(false);
          newTabs = [];
          this.activeTab.set('');
        } else if (cleanUrl.includes('/declarations/details')) {
          newTabs = this.getDeclarationsDetailsTabs();
        } else if (
          cleanUrl.includes('/declarations/edit') ||
          cleanUrl.includes('/delivery/edit')
        ) {
          newTabs = this.getDeclarationsEditTabs();
        } else {
          newTabs =
            matchedMenu.id === 'import'
              ? this.getImportTabs()
              : this.getExportTabs();
        }
      } else if (matchedMenu.id === 'transit') {
        this.showTabs.set(true);
        if (
          cleanUrl.includes('/transit-declarations/transit-arrival/details/') ||
          cleanUrl.includes('/transit-declarations/transit-departure/details/')
        ) {
          newTabs = cleanUrl.includes('/transit-departure/')
            ? this.getTransitDepartureDetailsTabs()
            : this.getTransitArrivalDetailsTabs();
        } else if (
          cleanUrl.includes('/transit-declarations/transit-arrival/create') ||
          cleanUrl.includes('/transit-declarations/transit-departure/create') ||
          cleanUrl.includes('/transit-declarations/transit-arrival/edit/') ||
          cleanUrl.includes('/transit-declarations/transit-departure/edit/')
        ) {
          if (
            cleanUrl.includes('/transit-declarations/transit-arrival/create') ||
            cleanUrl.includes('/transit-declarations/transit-departure/create')
          ) {
            // Create pages use layout-01 card overview (no footer section tabs).
            this.showTabs.set(false);
            newTabs = [];
            this.activeTab.set('');
          } else if (cleanUrl.includes('/transit-departure/')) {
            newTabs = this.getTransitDepartureCreateTabs();
          } else if (cleanUrl.includes('/transit-arrival/edit/')) {
            newTabs = this.getTransitArrivalEditTabs();
          } else {
            newTabs = this.getTransitArrivalCreateTabs();
          }
        } else {
          newTabs = this.getTransitTabs();
        }
      } else if (matchedMenu.id === 'emcs') {
        if (cleanUrl.includes('/emcs/create')) {
          // Layout-01 create is a single-page draft overview (no footer tab sections).
          this.showTabs.set(false);
          newTabs = [];
          this.activeTab.set('');
        } else if (cleanUrl.includes('/emcs/details/')) {
          this.showTabs.set(true);
          newTabs = this.getEmcsDetailsTabs();
        } else if (cleanUrl.includes('/emcs/edit/')) {
          this.showTabs.set(true);
          newTabs = this.getEmcsCreateTabs();
        } else {
          this.showTabs.set(true);
          newTabs = this.getEmcsTabs();
        }
      } else if (matchedMenu.id === 'rto') {
        this.showTabs.set(true);
        if (cleanUrl.includes('/rto/shipments/details/')) {
          newTabs = this.getRtoShipmentDetailsTabs();
        } else {
          newTabs = this.getRtoTabs();
        }
      } else if (matchedMenu.id === 'dashboard') {
        this.showTabs.set(true);
        newTabs = this.getDashboardTabs();
      } else if (matchedMenu.id === 'settings') {
        if (isEditPageWithoutTabs) {
          this.showTabs.set(false);
          newTabs = [];
        } else {
          this.showTabs.set(true);
          if (cleanUrl.includes('/settings/brokers/details')) {
            newTabs = this.getBrokerDetailsTabs();
          } else if (cleanUrl.includes('/settings/clients/details')) {
            newTabs = this.getClientDetailsTabs();
          } else if (cleanUrl.includes('/settings/users/details')) {
            newTabs = [{ id: '1', label: 'info', icon: 'info', route: 'info' }];
          } else if (
            cleanUrl.includes('/settings/declaration-templates/details')
          ) {
            newTabs = [{ id: '1', label: 'info', icon: 'info', route: 'info' }];
          } else if (cleanUrl.includes('/settings/parties/details')) {
            newTabs = [{ id: '1', label: 'info', icon: 'info', route: 'info' }];
          } else {
            newTabs = matchedMenu.tabs || this.defaultSettingsTabs;
          }
        }
      } else if (matchedMenu.id === 'tasks') {
        if (url.includes('/tickets/details')) {
          this.showTabs.set(false);
          newTabs = [];
        } else {
          this.showTabs.set(true);
          newTabs = this.defaultTicketsTabs;
        }
      } else {
        this.showTabs.set(matchedMenu.showTabs || false);
        if (matchedMenu.tabs) {
          newTabs = matchedMenu.tabs;
        }
      }

      if (baseUrl && newTabs.length > 0) {
        newTabs = this.applyBaseUrlToTabs(newTabs, baseUrl);
      }
      this.currentTabs.set(newTabs);

      // 3. Determine active tab ID
      // Use `newTabs` directly instead of re-reading `currentTabs()` to avoid
      // registering `currentTabs` as a tracked dependency of the parent effect,
      // which would cause an infinite CD loop (NG0103) since every run creates
      // a new array reference via the tab-builder helpers above.
      const tabs = newTabs;
      if (tabs.length > 0) {
        const urlTree = this.router.parseUrl(url);
        const tabParam = urlTree.queryParams['tab'];
        const currentSpecificValueFields =
          urlTree.queryParams['specificValueFields'];
        const tabIds = new Set(tabs.map((t) => t.id));

        const urlPath = cleanUrl;
        const routeMatchedTabs = tabs.filter(
          (t) =>
            t.route &&
            (urlPath.endsWith(t.route) ||
              urlPath.includes('/' + t.route + '/') ||
              urlPath.includes('/' + t.route + '?') ||
              (urlPath.startsWith('/shipments') &&
                t.route.endsWith('/shipments')) ||
              (urlPath.startsWith('/declarations') &&
                t.route.endsWith('/declarations')))
        );

        // Prefer tabs whose queryParams match (e.g. EMCS Import/Export share /emcs).
        const queryMatchedTabs = routeMatchedTabs.filter((t) => {
          const tabSpecific = t.queryParams?.['specificValueFields'];
          const tabNot = t.queryParams?.['specificNotFields'];
          const currentNot = urlTree.queryParams['specificNotFields'];

          if (currentSpecificValueFields) {
            return tabSpecific === currentSpecificValueFields;
          }
          if (tabSpecific) {
            return false;
          }
          if (tabNot) {
            return currentNot === tabNot;
          }
          return !currentNot;
        });

        const bestTab = (
          queryMatchedTabs.length > 0 ? queryMatchedTabs : routeMatchedTabs
        ).sort((a, b) => (b.route?.length || 0) - (a.route?.length || 0))[0];

        const isDetailsRoute =
          cleanUrl.includes('/details/') ||
          cleanUrl.includes('/declarations/edit/') ||
          cleanUrl.includes('/delivery/edit/');

        const resolvedTab =
          (tabParam && tabIds.has(tabParam) ? tabParam : null) ||
          (bestTab && tabIds.has(bestTab.id) ? bestTab.id : null) ||
          (!isDetailsRoute && matchedTab && tabIds.has(matchedTab.id)
            ? matchedTab.id
            : null) ||
          tabs[0].id;

        this.activeTab.set(resolvedTab);
      }
    } else {
      this.showTabs.set(false);
      this.activeMenu = '';
    }
  }

  /**
   * Handles get details base url logic.
   * @param url - The url
   */
  private getDetailsBaseUrl(url: string): string | null {
    // url should already be clean (no query params) when called from initializeFromRoute
    const segments = url.split('/').filter((s) => s !== '');

    // Patterns like: shipments/details/:id/..., declarations/details/:id/..., declarations/edit/:id/...
    const patterns = [
      'shipments/details',
      'declarations/details',
      'declarations/edit',
      'delivery/edit',
      'settings/brokers/details',
      'settings/clients/details',
      'settings/parties/details',
    ];

    for (const pattern of patterns) {
      if (url.includes(pattern)) {
        const patternSegments = pattern.split('/');
        const lastPatternSegment = patternSegments[patternSegments.length - 1];
        const idx = segments.indexOf(lastPatternSegment);
        // Ensure there's an ID segment after the pattern
        if (idx !== -1 && segments[idx + 1]) {
          // Verify the whole pattern matches before calculating baseUrl
          const isMatch = patternSegments.every(
            (seg, i) => segments[idx - (patternSegments.length - 1) + i] === seg
          );
          if (isMatch) {
            return '/' + segments.slice(0, idx + 2).join('/');
          }
        }
      }
    }

    return null;
  }

  /**
   * Handles apply base url to tabs logic.
   * @param tabs - The tabs
   * @param baseUrl - The base url
   */
  private applyBaseUrlToTabs(tabs: TabItem[], baseUrl: string): TabItem[] {
    return tabs.map((t) => {
      // If the tab has a relative route and NO children (children use sub-menus), make it absolute
      if (t.route && !t.route.startsWith('/') && !t.openDrawer) {
        return {
          ...t,
          route: `${baseUrl}/${t.route}`,
        };
      }
      return t;
    });
  }

  /**
   * Handles get deps logic.
   * @param overrides - The overrides
   */
  private getDeps(
    overrides?: Partial<NavigationConfigDeps>
  ): NavigationConfigDeps {
    return {
      procedureType: this.dataService.procedureType(),
      errorCounts: this.dataService.errorCounts(),
      shipmentCounts: this.dataService.shipmentCounts(),
      activeLayout: this.layoutService.activeLayout(),
      canAccessApiKeysAndWebhooks:
        this.roleService.canAccessApiKeysAndWebhooks(),
      currentUrl: this.router.url,
      statusCode: this.dataService.transitStatusCode(),
      isRtoEnabled: !!this.dataService.currentShipment()?.isRtoEnabled,
      isNctsEnabled: !!this.dataService.currentShipment()?.isNctsEnabled,
      ...overrides,
    };
  }

  /** Property default tickets tabs. */
  defaultTicketsTabs = DEFAULT_TICKETS_TABS;
  /** Property default email tabs. */
  defaultEmailTabs = DEFAULT_EMAIL_TABS;
  /** Property default settings tabs. */
  defaultSettingsTabs = DEFAULT_SETTINGS_TABS;

  /**
   * Handles get import tabs logic.
   */
  getImportTabs(): TabItem[] {
    return this.filterTabsByPermission(getImportTabs(this.getDeps()));
  }

  /**
   * Handles get export tabs logic.
   */
  getExportTabs(): TabItem[] {
    return this.filterTabsByPermission(getExportTabs(this.getDeps()));
  }

  /**
   * Handles get dashboard tabs logic.
   */
  getDashboardTabs(): TabItem[] {
    return getDashboardTabs((permission) =>
      this.roleService.isPermitted(permission)
    );
  }

  /**
   * Keeps only tabs the current role may view.
   * @param tabs - Candidate tab items
   * @returns Tabs with no permission, or a granted permission
   */
  private filterTabsByPermission(tabs: TabItem[]): TabItem[] {
    return tabs.filter((tab) => {
      if (!tab.permission) {
        return true;
      }
      return this.roleService.isPermitted(tab.permission);
    });
  }

  /**
   * Handles get shipment details tabs logic.
   */
  getShipmentDetailsTabs(): TabItem[] {
    return getShipmentDetailsTabs(this.getDeps());
  }

  /**
   * Handles get client details tabs logic.
   */
  getClientDetailsTabs(): TabItem[] {
    return getClientDetailsTabs();
  }

  /**
   * Handles get broker details tabs logic.
   */
  getBrokerDetailsTabs(): TabItem[] {
    return getBrokerDetailsTabs();
  }

  /**
   * Handles get declarations details tabs logic.
   */
  getDeclarationsDetailsTabs(): TabItem[] {
    return getDeclarationsDetailsTabs(this.getDeps());
  }

  /**
   * Handles get transit tabs logic.
   */
  getTransitTabs(): TabItem[] {
    return [
      /* {
        id: 'overview',
        label: 'overview',
        icon: 'info',
        route: '/transit-declarations/overview',
      }, */
      {
        id: 'transit-departure',
        label: 'transitDeparture',
        icon: 'transit-departure-icon',
        route: '/transit-declarations/transit-departure',
        queryParams: {
          specificValueFields: JSON.stringify({
            type: 'DEPARTURE',
          }),
        },
      },
      {
        id: 'transit-arrival',
        label: 'transitArrival',
        icon: 'transit-arrival-icon',
        route: '/transit-declarations/transit-arrival',
        queryParams: {
          specificValueFields: JSON.stringify({
            type: 'ARRIVAL',
          }),
        },
      },
    ];
  }

  /**
   * Handles get emcs tabs logic.
   */
  getEmcsTabs(): TabItem[] {
    return getEmcsTabs();
  }

  /**
   * Handles get emcs create tabs logic.
   */
  getEmcsCreateTabs(): TabItem[] {
    return getEmcsCreateTabs();
  }

  /**
   * Handles get emcs details tabs logic.
   */
  getEmcsDetailsTabs(): TabItem[] {
    return getEmcsDetailsTabs();
  }

  /**
   * Handles get rto tabs logic.
   */
  getRtoTabs(): TabItem[] {
    return getRtoTabs();
  }

  /**
   * Handles get rto shipment details tabs logic.
   */
  getRtoShipmentDetailsTabs(): TabItem[] {
    return getRtoShipmentDetailsTabs();
  }

  /**
   * Handles get transit arrival create tabs logic.
   */
  getTransitArrivalCreateTabs(): TabItem[] {
    return getTransitArrivalCreateTabs();
  }

  /**
   * Handles get transit arrival edit tabs logic.
   */
  getTransitArrivalEditTabs(): TabItem[] {
    return getTransitArrivalEditTabs();
  }

  /**
   * Handles get transit arrival details tabs logic.
   */
  getTransitArrivalDetailsTabs(): TabItem[] {
    return getTransitArrivalDetailsTabs(this.getDeps());
  }

  /**
   * Handles get transit departure create tabs logic.
   */
  getTransitDepartureCreateTabs(): TabItem[] {
    return getTransitDepartureCreateTabs();
  }

  /**
   * Handles get transit departure details tabs logic.
   */
  getTransitDepartureDetailsTabs(): TabItem[] {
    return getTransitDepartureDetailsTabs(this.getDeps());
  }

  /**
   * Handles get declarations edit tabs logic.
   */
  getDeclarationsEditTabs(): TabItem[] {
    return getDeclarationsEditTabs(
      this.getDeps({ currentUrl: this.router.url })
    );
  }

  /**
   * Handles get declaration create tabs logic.
   * @param source - The source
   */
  getDeclarationCreateTabs(source?: string | null): TabItem[] {
    return getDeclarationCreateTabs(this.getDeps(), source);
  }

  /**
   * Handles is ncts logic.
   */
  isNCTS() {
    const procType = this.dataService.procedureType();
    return ['D1', 'D2', 'D3', 'D4'].includes(procType ?? '');
  }

  /**
   * Handles on alert click logic.
   */
  onAlertClick() {
    if (this.dialogRef) {
      this.dialogRef.close();
    }

    this.dialogRef = this.dialogService.open(UpdatesComponent, {
      width: '400px',
      height: '100%',
      baseZIndex: 10000,
      dismissableMask: true,
      modal: true,
      closeOnEscape: true,
      header: 'updates',
      closable: true,
      styleClass: 'profile-dialog custom-header border-none!',
      maskStyleClass: 'profile-mask-right',
    });
  }

  /**
   * Handles on drawer close logic.
   */
  onDrawerClose() {
    this.drawerVisible = false;
    this.refreshLastViewed();
  }

  /** Property refresh unread callback. */
  refreshUnreadCallback = () => {
  };

  /** Property menus. */
  menus: FooterMenu[] = DEFAULT_MENUS;

  /**
   * Permission-aware operational landing path from account preferences.
   * @param landingPage - Force list or dashboard mode when set
   * @returns Absolute route, or import shipments as last UI fallback
   */
  private resolveOperationalHomeRoute(
    landingPage?: 'list' | 'dashboard'
  ): string {
    const path = resolveOperationalLandingPath(
      (permission) => this.roleService.isPermitted(permission),
      {
        defaultDashboard: this.defaultDashboard(),
        moduleLandingPage:
          landingPage ?? this.dashboardService.moduleLandingPage(),
      }
    );
    return path ?? '/import/shipments';
  }

  /** Dashboard route for the user's first permitted operational module. */
  private getDefaultModuleDashboardRoute(): string {
    return this.resolveOperationalHomeRoute('dashboard');
  }

  /** List route for the user's first permitted operational module. */
  private getDefaultModuleListRoute(): string {
    return this.resolveOperationalHomeRoute('list');
  }

  /**
   * App / home landing: operational module dashboard or list from preferences.
   */
  private getDefaultModuleLandingRoute(): string {
    return this.resolveOperationalHomeRoute();
  }

  /**
   * Maps an operational landing path to the footer module chrome (menu + tabs).
   * @param route - Absolute landing path from {@link getDefaultModuleLandingRoute}
   * @returns Active menu id and tab list for that module shell
   */
  private resolveHomeShellForRoute(route: string): {
    menuId: string;
    tabs: TabItem[];
  } {
    if (route.startsWith('/dashboard/')) {
      return { menuId: 'dashboard', tabs: this.getDashboardTabs() };
    }
    if (route.startsWith('/transit-declarations')) {
      return { menuId: 'transit', tabs: this.getTransitTabs() };
    }
    if (route.startsWith('/emcs')) {
      return { menuId: 'emcs', tabs: this.getEmcsTabs() };
    }
    if (route.startsWith('/rto')) {
      return { menuId: 'rto', tabs: this.getRtoTabs() };
    }
    if (route.startsWith('/export/')) {
      return { menuId: 'export', tabs: this.getExportTabs() };
    }
    return { menuId: 'import', tabs: this.getImportTabs() };
  }

  /** Settings list tabs currently visible for this user (client filter applied). */
  private getSettingsMenuTabs(): TabItem[] {
    const settingsMenu = this.menus.find((m) => m.id === 'settings');
    return settingsMenu?.tabs?.length
      ? settingsMenu.tabs
      : this.defaultSettingsTabs;
  }

  /**
   * Global Modules list landing: first settings list tab (usually brokers).
   */
  private getGlobalModulesListRoute(): string {
    return this.getSettingsMenuTabs()[0]?.route || '/settings/brokers';
  }

  /**
   * Handles get menu link logic.
   * @param menu - The menu
   */
  getMenuLink(menu: FooterMenu): string {
    if (menu.id === 'home') {
      return this.getDefaultModuleLandingRoute();
    }
    // Dashboard menu always opens the default module's dashboard overview.
    if (menu.id === 'dashboard') {
      return this.getDefaultModuleDashboardRoute();
    }
    if (menu.id === 'transit') {
      return '/transit-declarations/transit-departure';
    }
    if (menu.id === 'emcs') {
      return '/emcs';
    }
    // Global Modules always opens its list (dashboards live under Dashboard menu).
    if (menu.id === 'settings') {
      return this.getGlobalModulesListRoute();
    }
    // Tasks always opens the list (Tasks dashboard lives under Dashboard menu).
    if (menu.id === 'tasks') {
      return '/tickets/tasks';
    }
    // If menu has tabs, we usually want to navigate to the first tab's route
    let menuTabs = menu.tabs;
    if (menu.id === 'import') {
      menuTabs = this.getImportTabs();
    } else if (menu.id === 'export') {
      menuTabs = this.getExportTabs();
    } else if (menu.id === 'emcs') {
      menuTabs = this.getEmcsTabs();
    } else if (menu.id === 'transit') {
      menuTabs = this.getTransitTabs();
    }

    if (menu.showTabs && menuTabs && menuTabs.length > 0) {
      // Module menus always land on the list. Dashboards are only reached via
      // the Dashboard footer / sidebar menu (not via moduleLandingPage).
      if (menuTabs.length > 1) {
        const listTab = menuTabs.find((t) => t.id !== 'dashboard');
        return listTab?.route || menuTabs[0].route || menu.route;
      }
      return menuTabs[0].route || menu.route;
    }
    return menu.route;
  }

  /**
   * Resolves the landing tab for a menu (route + query params).
   * @param menu - Footer menu that was clicked
   * @returns Absolute route and optional query params for navigation
   */
  private getMenuLandingTarget(menu: FooterMenu): {
    route: string;
    queryParams?: Record<string, string>;
  } {
    let menuTabs = menu.tabs;
    if (menu.id === 'import') {
      menuTabs = this.getImportTabs();
    } else if (menu.id === 'export') {
      menuTabs = this.getExportTabs();
    } else if (menu.id === 'emcs') {
      menuTabs = this.getEmcsTabs();
    } else if (menu.id === 'transit') {
      menuTabs = this.getTransitTabs();
    } else if (menu.id === 'dashboard') {
      menuTabs = this.getDashboardTabs();
    }

    const route = this.getMenuLink(menu);
    if (!menuTabs?.length || route.startsWith('/dashboard/')) {
      return { route };
    }

    // EMCS Import/Export/EU share `/emcs` — land on EU Movements by default.
    const targetTab =
      menu.id === 'emcs' && route === '/emcs'
        ? menuTabs.find((t) => t.id === 'emcs-list') ?? menuTabs[0]
        : menuTabs.find((t) => t.route === route) ?? menuTabs[0];

    return {
      route,
      queryParams: targetTab?.queryParams as Record<string, string> | undefined,
    };
  }

  /**
   * Handles on menu click logic.
   * @param menu - The menu
   * @param event - The event
   */
  onMenuClick(menu: FooterMenu, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.setActive(menu.id);
  }

  /**
   * Handles set active logic.
   * @param menuId - The menu id
   */
  setActive(menuId: string) {
    const menu = this.menus.find((m) => m.id === menuId);

    if (
      (menuId === 'rto' || menu?.route?.includes('/rto')) &&
      !this.roleService.isRtoEnabledForCurrentUser()
    ) {
      this.rtoAccessService.openRtoNotConfiguredModal();
      return;
    }

    this.activeMenu = menuId;
    this.dataService.setActiveModule(menuId);

    if (menu) {
      if (menuId === 'home') {
        const target = this.getDefaultModuleLandingRoute();
        const shell = this.resolveHomeShellForRoute(target);
        this.activeMenu = shell.menuId;
        this.dataService.setActiveModule(shell.menuId);
        this.showTabs.set(shell.tabs.length > 0);
        this.currentTabs.set(shell.tabs);
        const targetTab =
          shell.tabs.find((tab) => tab.route === target) ?? shell.tabs[0];
        this.activeTab.set(targetTab?.id ?? '');
        this.router.navigateByUrl(target);
        return;
      }

      // Global Modules: navigate only — tabs come from initializeFromRoute.
      // Do not swap in dashboard tabs before navigation (that surfaced Shipments).
      if (menuId === 'settings') {
        this.showTabs.set(true);
        this.router.navigateByUrl(this.getMenuLink(menu));
        return;
      }

      // Show tabs if menu has showTabs enabled
      this.showTabs.set(menu.showTabs || false);

      // Set the tabs for this menu
      let menuTabs = menu.tabs;
      if (menuId === 'import') {
        menuTabs = this.getImportTabs();
      } else if (menuId === 'export') {
        menuTabs = this.getExportTabs();
      } else if (menuId === 'emcs') {
        menuTabs = this.getEmcsTabs();
      } else if (menuId === 'transit') {
        menuTabs = this.getTransitTabs();
      } else if (menuId === 'dashboard') {
        menuTabs = this.getDashboardTabs();
      }

      if (menu.showTabs && menuTabs && menuTabs.length > 0) {
        this.currentTabs.set(menuTabs);
        const landing = this.getMenuLandingTarget(menu);

        // Find the tab that matches the target route to set it as active
        const targetTab =
          menuTabs.find(
            (t) =>
              t.route === landing.route &&
              (!landing.queryParams ||
                JSON.stringify(t.queryParams ?? {}) ===
                  JSON.stringify(landing.queryParams))
          ) ??
          menuTabs.find((t) => t.route === landing.route) ??
          menuTabs[0];
        this.activeTab.set(targetTab.id);

        this.router.navigate([landing.route], {
          queryParams: landing.queryParams ?? {},
        });
      } else {
        // No tabs, navigate to menu route
        const landing = this.getMenuLandingTarget(menu);
        this.router.navigate([landing.route], {
          queryParams: landing.queryParams ?? {},
        });
      }
    }
  }

  // Expose currentTabs as the tabs to display
  /**
   * Gets the tabs.
   * @returns The tabs value
   */
  get tabs(): TabItem[] {
    return this.currentTabs();
  }

  /**
   * Handles on tab click logic.
   * @param tab - The tab
   */
  onTabClick(tab: TabItem) {
    if (
      !tab.route?.startsWith('/dashboard') &&
      (tab.route?.includes('rto') || tab.id?.includes('rto')) &&
      !this.roleService.isRtoEnabledForCurrentUser()
    ) {
      this.rtoAccessService.openRtoNotConfiguredModal();
      return;
    }

    if (tab.openDrawer || tab.label === 'Alerts') {
      this.openDrawer(tab);
    } else {
      this.onTabChange(tab.id);
    }
  }

  /**
   * Handles open drawer logic.
   * @param tab - The tab
   */
  openDrawer(tab: TabItem) {
    if (tab.label === 'alerts') {
      this.onAlertClick();
    } else {
      this.drawerType = tab.id;
      this.drawerVisible = true;
    }
  }

  /** Property active tab. */
  activeTab = signal<string>('1');

  /**
   * Handles on tab change logic.
   * @param tabId - The tab id
   */
  onTabChange(tabId: string) {
    const tabs = this.currentTabs();
    const tab = tabs.find((t) => t.id === tabId);

    if (!tab) return;

    if (
      !tab.route?.startsWith('/dashboard') &&
      (tab.route?.includes('rto') || tab.id?.includes('rto')) &&
      !this.roleService.isRtoEnabledForCurrentUser()
    ) {
      this.rtoAccessService.openRtoNotConfiguredModal();
      return;
    }

    // ✅ 1. Handle drawer tabs immediately (even if route is empty)
    if (tab.openDrawer || tab.label === 'Alerts') {
      this.openDrawer(tab);
      this.activeTab.set(tabId); // Update active state visually
      return;
    }

    if (tab.label === 'Last Viewed') {
      const route =
        tab.route ||
        (this.activeMenu === 'shipments'
          ? '/shipments/last-viewed'
          : '/declarations/last-viewed');
      this.router.navigate([route], { queryParams: { tabMethod: true } });
      this.activeTab.set(tabId);
      return;
    }

    // Set active tab before navigation for immediate feedback
    this.activeTab.set(tabId);

    // ✅ Handle components that use custom event for tab switching (Details/Edit wrappers)
    const urlTree = this.router.parseUrl(this.router.url);
    const path = urlTree.root.children['primary']
      ? urlTree.root.children['primary'].segments.map((s) => s.path).join('/')
      : '';

    if (
      !tab.route ||
      path.includes('shipments/details') ||
      path.includes('declarations/details') ||
      path.includes('delivery/edit') ||
      path.includes('declarations/edit') ||
      path.includes('transit-declarations/transit-arrival/create') ||
      path.includes('transit-declarations/transit-arrival/edit') ||
      path.includes('transit-declarations/transit-departure/create') ||
      path.includes('transit-declarations/transit-departure/edit') ||
      path.includes('transit-declarations/transit-arrival/details') ||
      path.includes('transit-declarations/transit-departure/details') ||
      path.includes('emcs/create') ||
      path.includes('emcs/edit') ||
      path.includes('emcs/details') ||
      path.includes('settings/brokers/details') ||
      path.includes('settings/clients/details')
    ) {
      window.dispatchEvent(
        new CustomEvent('footer-tab-change', {
          detail: { tabId, tab },
        })
      );
      if (
        !tab.route ||
        path.includes('declarations/edit/') ||
        path.includes('delivery/edit/') ||
        path.includes('transit-declarations/transit-arrival/create') ||
        path.includes('transit-declarations/transit-arrival/edit/') ||
        path.includes('transit-declarations/transit-departure/create') ||
        path.includes('transit-declarations/transit-departure/edit/') ||
        path.includes('transit-declarations/transit-arrival/details/') ||
        path.includes('transit-declarations/transit-departure/details/') ||
        path.includes('emcs/create') ||
        path.includes('emcs/edit/') ||
        path.includes('emcs/details/') ||
        (this.dataService.declarationEditMode() &&
          path.includes('declarations/details')) ||
        // Declarations can stay event-only in tabMethod mode. Shipment details
        // must still navigate so the child route cannot keep a stale RTO tab
        // (e.g. /overview) mounted while Attachments is selected.
        (this.isTabMethod() && path.includes('declarations/details'))
      )
        return;
    }
    let baseUrl = '';

    // ✅ 2. Handle shipment details child routing
    if (path.includes('shipments/details')) {
      const segments = path.split('/');
      const detailsIndex = segments.indexOf('details');
      if (detailsIndex !== -1 && segments[detailsIndex + 1]) {
        baseUrl = '/' + segments.slice(0, detailsIndex + 2).join('/');
      }
    } else if (path.includes('declarations/details')) {
      const segments = path.split('/');
      const detailsIndex = segments.indexOf('details');
      if (detailsIndex !== -1 && segments[detailsIndex + 1]) {
        baseUrl = '/' + segments.slice(0, detailsIndex + 2).join('/');
      }
    }
    // ✅ 4. Handle broker details child routing
    else if (path.includes('settings/brokers/details')) {
      const segments = path.split('/');
      const detailsIndex = segments.indexOf('details');
      if (detailsIndex !== -1 && segments[detailsIndex + 1]) {
        baseUrl = '/' + segments.slice(0, detailsIndex + 2).join('/');
      }
    }
    // ✅ 5. Handle client details child routing
    else if (path.includes('settings/clients/details')) {
      const segments = path.split('/');
      const detailsIndex = segments.indexOf('details');
      if (detailsIndex !== -1 && segments[detailsIndex + 1]) {
        baseUrl = '/' + segments.slice(0, detailsIndex + 2).join('/');
      }
    }

    if (baseUrl && tab.route && !tab.route.startsWith('/')) {
      const isRtoDetails = baseUrl.includes('/rto/shipments/details');
      // Preserve cross-module ownership (e.g. Import → RTO HAWB). Never force
      // `rto` when the user arrived from another module — that would rewrite
      // breadcrumbs to PGTS on every footer tab click / refresh.
      const rtoParent =
        this.dataService.breadcrumbParentModule() ||
        this.dataService.activeModule() ||
        'rto';
      this.router.navigate([baseUrl, tab.route], {
        queryParams: { tabMethod: true },
        ...(isRtoDetails ? { state: { breadcrumbParent: rtoParent } } : {}),
      });
    } else if (tab.route && (!tab.children || tab.children.length === 0)) {
      // ✅ 4. Normal navigation - ONLY if no children (to avoid clearing child query params)
      this.router.navigate(
        [tab.route.startsWith('/') ? tab.route : '/' + tab.route],
        tab.queryParams ? { queryParams: tab.queryParams } : {}
      );
    }
  }
}
